import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import api from '../services/api';
import Toast from '../components/Toast';
import { CATEGORY_LABELS } from '../constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import './AdminStats.css';

const COLORS = ['#e94560', '#4da6ff', '#4caf50', '#ff9800', '#ce93d8', '#00bcd4', '#ff5722', '#a0a0b0', '#ffeb3b', '#8bc34a'];

export default function AdminStats() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [month, setMonth] = useState('');
  const [isAllTime, setIsAllTime] = useState(false);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isAllTime) {
        params.month = 'all';
      } else if (month) {
        params.month = month;
      }
      const res = await api.get('/admin/stats', { params });
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) navigate('/today', { replace: true });
      else showToast('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  }, [month, isAllTime, navigate]);

  useEffect(() => { load(); }, [load]);

  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    monthOptions.push({ value: val, label });
  }

  if (loading) {
    return (
      <Layout>
        <div className="loading-container"><div className="spinner" /></div>
      </Layout>
    );
  }

  if (!stats) {
    return (
      <Layout>
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <div className="empty-state-title">No hay datos disponibles</div>
          <button className="btn btn-secondary btn-sm mt-8" onClick={() => navigate('/admin')}>
            ← Volver a usuarios
          </button>
        </div>
      </Layout>
    );
  }

  const catData = (stats.category_distribution || []).map(c => ({
    name: CATEGORY_LABELS[c.category] || c.category,
    value: c.count,
  }));
  const dowData = (stats.day_of_week_distribution || []);

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}
      <div className="page">
        <div className="page-header">
          <div className="page-title">Estadísticas</div>
          <span className="text-muted text-sm">{stats.period}</span>
        </div>

        <div className="admin-tabs px-16">
          <button type="button" className="admin-tab" onClick={() => navigate('/admin')}>Usuarios</button>
          <button type="button" className="admin-tab active" onClick={() => {}}>Estadísticas</button>
          <button type="button" className="admin-tab" onClick={() => navigate('/admin/templates')}>Plantillas</button>
          <button type="button" className="admin-tab" onClick={() => navigate('/admin/options')}>Opciones</button>
        </div>

        <div className="px-16 mb-16" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="form-input" value={month} onChange={e => { setMonth(e.target.value); setIsAllTime(false); }} disabled={isAllTime} style={{ flex: 1 }}>
            <option value="">Mes actual</option>
            {monthOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            className={'chip' + (isAllTime ? ' active' : '')}
            onClick={() => setIsAllTime(s => !s)}
          >
            Todo
          </button>
        </div>

        {/* KPIs */}
        <div className="stat-cards">
          <div className="stat-card">
            <div className="stat-value">{stats.users.total}</div>
            <div className="stat-label">Usuarios totales</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#4caf50' }}>{stats.users.active}</div>
            <div className="stat-label">Activos</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#ff9800' }}>{stats.users.pending}</div>
            <div className="stat-label">Pendientes</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#4da6ff' }}>{stats.workouts.completed_in_period}</div>
            <div className="stat-label">Entrenos completados</div>
          </div>
        </div>

        <div className="stat-cards">
          <div className="stat-card">
            <div className="stat-value">{stats.workouts.active_users_period}</div>
            <div className="stat-label">Usuarios activos</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.users.new_in_period}</div>
            <div className="stat-label">Nuevos usuarios</div>
          </div>
        </div>

        {/* Daily charts (only for month view) */}
        {!isAllTime && (stats.daily_workouts || []).length > 0 && (
          <div className="chart-card card mx-16 mb-16">
            <h3 className="chart-title">Entrenamientos por día</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.daily_workouts}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                <XAxis dataKey="day" stroke="#a0a0b0" fontSize={11} />
                <YAxis stroke="#a0a0b0" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, color: '#eaeaea' }} formatter={(value) => [value, 'Entrenos']} />
                <Bar dataKey="count" fill="#e94560" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {!isAllTime && (stats.daily_new_users || []).length > 0 && (
          <div className="chart-card card mx-16 mb-16">
            <h3 className="chart-title">Nuevos usuarios por día</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={stats.daily_new_users}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                <XAxis dataKey="day" stroke="#a0a0b0" fontSize={11} />
                <YAxis stroke="#a0a0b0" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, color: '#eaeaea' }} formatter={(value) => [value, 'Usuarios']} />
                <Line type="monotone" dataKey="count" stroke="#4da6ff" strokeWidth={2} dot={{ fill: '#4da6ff', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Monthly charts (for all-time view) */}
        {isAllTime && (stats.monthly_workouts || []).length > 0 && (
          <div className="chart-card card mx-16 mb-16">
            <h3 className="chart-title">Entrenamientos por mes</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.monthly_workouts.map(m => ({ ...m, label: m.month.substring(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                <XAxis dataKey="label" stroke="#a0a0b0" fontSize={11} />
                <YAxis stroke="#a0a0b0" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, color: '#eaeaea' }} formatter={(value) => [value, 'Entrenos']} />
                <Bar dataKey="count" fill="#e94560" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {isAllTime && (stats.monthly_new_users || []).length > 0 && (
          <div className="chart-card card mx-16 mb-16">
            <h3 className="chart-title">Nuevos usuarios por mes</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={stats.monthly_new_users.map(m => ({ ...m, label: m.month.substring(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                <XAxis dataKey="label" stroke="#a0a0b0" fontSize={11} />
                <YAxis stroke="#a0a0b0" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, color: '#eaeaea' }} formatter={(value) => [value, 'Usuarios']} />
                <Line type="monotone" dataKey="count" stroke="#4da6ff" strokeWidth={2} dot={{ fill: '#4da6ff', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Category distribution */}
        {catData.length > 0 && (
          <div className="chart-card card mx-16 mb-16">
            <h3 className="chart-title">Distribución por categoría</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={catData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                  {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, color: '#eaeaea' }} />
                <Legend wrapperStyle={{ fontSize: '0.75rem', color: '#a0a0b0' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Day of week */}
        {dowData.length > 0 && (
          <div className="chart-card card mx-16 mb-16">
            <h3 className="chart-title">Días más populares</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dowData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                <XAxis dataKey="day_name" stroke="#a0a0b0" fontSize={12} />
                <YAxis stroke="#a0a0b0" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, color: '#eaeaea' }} formatter={(value) => [value, 'Entrenos']} />
                <Bar dataKey="count" fill="#00bcd4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top users list */}
        {(stats.top_users || []).length > 0 && (
          <div className="px-16 mb-16">
            <h3 className="mb-8 fw-700">Top entrenamientos</h3>
            <div className="popular-exercises-list">
              {(stats.top_users || []).map((u, i) => (
                <div key={i} className="popular-ex-item">
                  <span className="popular-ex-rank">#{i + 1}</span>
                  <span className="popular-ex-name">{u.username}</span>
                  <span className="popular-ex-count">{u.workouts} entrenos</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Popular exercises */}
        {(stats.popular_exercises || []).length > 0 && (
          <div className="px-16 mb-16">
            <h3 className="mb-8 fw-700">Ejercicios más populares</h3>
            <div className="popular-exercises-list">
              {(stats.popular_exercises || []).map((ex, i) => (
                <div key={i} className="popular-ex-item">
                  <span className="popular-ex-rank">#{i + 1}</span>
                  <div className="popular-ex-info">
                    <span className="popular-ex-name">{ex.name}</span>
                    <span className={`badge badge-${(ex.category || 'other').toLowerCase()}`}>
                      {CATEGORY_LABELS[ex.category] || ex.category || 'Otro'}
                    </span>
                  </div>
                  <span className="popular-ex-count">{ex.set_count} series</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top streaks */}
        {(stats.top_streaks || []).length > 0 && (
          <div className="px-16 mb-16">
            <h3 className="mb-8 fw-700">Mejores rachas</h3>
            <div className="popular-exercises-list">
              {(stats.top_streaks || []).map((s, i) => (
                <div key={i} className="popular-ex-item">
                  <span className="popular-ex-rank">#{i + 1}</span>
                  <span className="popular-ex-name">{s.username}</span>
                  <span className="popular-ex-count" style={{ color: '#e94560' }}>{s.streak}🔥</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
